import BaseStore from 'customer-flows/stores/base-store'
import { tracked } from '@glimmer/tracking'
import { context } from 'customer-flows/utils/core'
import AvailabilityStore from 'customer-flows/stores/availability'

export default class TicketsStoreClass extends BaseStore {
  @context('activity') isRental: ActivityStore['isRental']
  @context('activity') tickets: ActivityStore['tickets']
  @context('availability') selectedDate: AvailabilityStore['selectedDate']
  @context('availability') availabilityForSelectedDate: AvailabilityStore['availabilityForSelectedDate']
  @context('availability') validTimeslots: AvailabilityStore['validTimeslots']
  @context('availability') timeslotsForSelectedTime: AvailabilityStore['timeslotsForSelectedTime']
  @context('availability') isAvailableStatus: AvailabilityStore['isAvailableStatus']
  @context('availability') getPricesForTicket: AvailabilityStore['getPricesForTicket']
  @context('tickets/rental:*') rentalDomain: RentalTicketsStore
  @context('tickets/activity:*') activityDomain: ActivityTicketsStore

  private get domain(): RentalTicketsStore | ActivityTicketsStore {
    return this.isRental ? this.rentalDomain : this.activityDomain
  }

  @context('program/booking-flow') ticketSelection: BookingFlowStore['ticketSelection']
  @context('program/booking-flow') disableAutoAddTicket: BookingFlowStore['disableAutoAddTicket']

  // This is used directly by the quote. So it is in the shape of the quote input
  @tracked selectedTickets: QuoteSparseTicketInput[] = []

  // set to true when the user explicitly removes all tickets, to prevent auto-add from re-adding them
  @tracked userHasManuallyEmptiedCart: boolean = false

  get confirmedTickets() {
    return this.selectedTickets.filter(({ reservationStatus }) => reservationStatus === 'CONFIRMED')
  }

  get hasSelectedTickets(): boolean {
    return this.confirmedTickets.length > 0
  }

  get canAutoAddTicket(): boolean {
    return (
      !this.disableAutoAddTicket &&
      !this.hasSelectedTickets &&
      !this.userHasManuallyEmptiedCart
    )
  }

  get hasValidTicketSelection(): boolean {
    if (!this.validTimeslots || this.validTimeslots?.length <= 0) return true
    if (!this.confirmedTickets || this.confirmedTickets?.length <= 0) return true
    return this.confirmedTickets.every(ticket => this.isTicketAvailable(ticket.resourceOptionId!))
  }

  /**
   * Returns the tickets that are selectable based on the ticketSelection config.
   */
  get selectableTickets() {
    if (this.ticketSelection.length === 0) return this.tickets
    return this.tickets.filter(ticket => this.ticketSelection.includes(ticket.id))
  }

  /**
   * If there is a whitelist on the timeslot, this returns only the tickets that are whitelisted.
   * If there is no whitelist, it returns all tickets.
   * If there are no timeslots matching the selected time, it returns all tickets.
   *
   * Uses timeslotsForSelectedTime (not validTimeslots) so that a previously-selected ticket
   * that is unavailable at the current time is still correctly identified as non-whitelisted —
   * even when validTimeslots is empty because that ticket's qty check fails.
   */
  get whiteListedTickets(): Ticket[] {
    if (!this.timeslotsForSelectedTime || this.timeslotsForSelectedTime.length <= 0) return this.selectableTickets

    return this.selectableTickets.filter(ticket => this.timeslotsForSelectedTime.some(slot => {
      if (!slot.whitelistedResourceOptionIds || slot.whitelistedResourceOptionIds.length === 0) return true

      return slot.whitelistedResourceOptionIds?.includes(ticket.id)
    }))
  }

  /**
   * Returns the total quantity per resource option for all confirmed tickets.
   *
   * Tickets are grouped by `resourceOptionId`, and only tickets with a
   * `reservationStatus` of `"CONFIRMED"` are included.
   *
   * @returns An array of objects containing a `resourceOptionId` and the
   * corresponding total `quantity` of confirmed tickets.
   */
  get resourceOptionQuantities() {
    const counts = this.confirmedTickets
      .filter(({ resourceOptionId }) => !!resourceOptionId)
      .reduce((acc, { resourceOptionId }) => {
        acc[resourceOptionId!] = (acc[resourceOptionId!] ?? 0) + 1
        return acc
      }, {} as Record<string, number>)

    // Empty list signals the BE to return all ticket types — preserve that when nothing is selected.
    // When at least one ticket is selected, pad remaining tickets to qty 0 so the BE still returns
    // availability data for all types (otherwise it scopes the response to only the selected IDs).
    if (Object.keys(counts).length === 0) return []

    return this.tickets
      .filter(({ id }) => !!id)
      .map(({ id }) => ({
        resourceOptionId: id,
        quantity: counts[id] ?? 0
      }))
  }

  /**
   * returns a simplified array of selected tickets with [number] on names when repeated
   */
  get selectedTicketNames(): SelectedTicket[] {
    const tickets = this.tickets ?? []

    if (!tickets) {
      return []
    }

    const formattedTickets = this.selectedTickets
      ?.filter(({ reservationStatus }) => reservationStatus === 'CONFIRMED')
      .map((selectedTicket: QuoteSparseTicketInput) => {
        const foundTicket = tickets.find(ticket => ticket.id === selectedTicket.resourceOptionId)

        return {
          id: selectedTicket.resourceOptionId ?? '',
          refId: selectedTicket.refid,
          reservationStatus: selectedTicket.reservationStatus,
          name: foundTicket?.name,
          category: foundTicket?.category
        } as SelectedTicket
      }) ?? []

    const sortedTickets = formattedTickets.sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
    )

    const nameCountMap = new Map<string, number>()
    const nameOccurrenceMap = new Map<string, number>()

    // count repeated names
    sortedTickets.forEach(ticket => {
      const name = ticket.name ?? ''
      nameCountMap.set(name, (nameCountMap.get(name) ?? 0) + 1)
    })

    // add numbering only for repeated
    return sortedTickets.map(ticket => {
      const name = ticket.name ?? ''
      const count = nameCountMap.get(name) ?? 0

      if (count > 1) {
        const occurrence = nameOccurrenceMap.get(name) ?? 0
        nameOccurrenceMap.set(name, occurrence + 1)
        return {
          ...ticket,
          name: `${name} #${occurrence + 1}`
        }
      }

      return ticket
    })
  }

  /**
   * Returns the minimum number of tickets required to book at the selected date.
   * The minimum is the same for all timeslots of a given date.
   * If no date is selected, returns 0.
   *
   * NOTE: before we were using this.validTimeslots which is not correct; as we need to display this the banner
   *  despite no time being selected
   */
  get minRequiredTickets(): number {
    return this.availabilityForSelectedDate
      .filter((slot) => slot != null && (slot.minimumTicketsRequired ?? 0) > 0)
      .map((slot) => slot.minimumTicketsRequired!)[0] ?? 0
  }

  /**
   * returns whether if the number of confirmed tickets meets the minimum required for the selected timeslot or not
   */
  get hasMetMinimumTicketRequirement(): boolean {
    const enforcedSlots = this.validTimeslots.filter(
      (slot) => slot.status === 'MIN_REQUIRED_NOT_BOOKABLE'
    )

    if (enforcedSlots.length === 0) return true

    const minRequired = enforcedSlots[0]?.minimumTicketsRequired ?? 0
    return this.confirmedTickets.length >= minRequired
  }

  /**
   * Returns true if there are CONFIRMED tickets that are not on the selected timeslot's whitelist.
   */
  get hasConfirmedNonWhitelistedTickets(): boolean {
    return this.confirmedTickets.some(ticket => !this.whiteListedTickets.some(whitelistedTicket => whitelistedTicket.id === ticket.resourceOptionId))
  }

  get firstAvailableTicket() {
    return this.whiteListedTickets?.find(ticket => this.isTicketAvailable(ticket.id))
  }

  get spotsMax(): number {
    return this.validTimeslots.reduce((acc, slot) => Math.min(acc, slot.spots ?? Infinity), Infinity)
  }

  getCountForTicket = (id: string) => {
    return this.confirmedTickets.filter(({ resourceOptionId }) => resourceOptionId === id).length
  }

  getAvailabilityForTicket(_ticketId: string): AvailabilityTime[] {
    return (
      this.validTimeslots?.filter((_availabilityTime) => {
        // TODO remove commented lines when BE has more availability data
        // returning true for now until BE can return more accurate data on ticket availability
        return true

        // const isWhitelistedTicket = availabilityTime.whitelistedResourceOptionIds?.includes(ticketId)
        // if (isWhitelistedTicket) return true
        //
        // const isAvailableTicket = availabilityTime.availability.some(({ resourceOptionId }) => resourceOptionId === ticketId)
        // if (isAvailableTicket) return true
        //
        // return false
      }) ?? []
    )
  }

  /**
   * Returns the minimum required quantity for a ticket.
   *
   * This is different from the `minRequiredTickets` property, which is the minimum number of tickets
   * required for the timeslot to be bookable.
   *
   * This property is not related to timeslots.
   */
  getMinQuantityForTicket(ticketId: string): number {
    const ticket = this.tickets.find(ticket => ticket.id === ticketId)
    return ticket?.minQuantity ?? 0
  }

  getMaxQuantityForTicket(ticketId: string): number | undefined {
    const ticket = this.tickets.find(ticket => ticket.id === ticketId)
    // 0 means no configured max (same as unset) — treat as undefined so the ?? chain
    // falls through to getMaxAvailableQtyForTicket
    return ticket?.maxQuantity || undefined
  }

  isMinRequiredForTicket(ticketId: string): boolean {
    const ticket = this.tickets.find(ticket => ticket.id === ticketId)
    return ticket?.isMinRequired ?? false
  }

  isTicketAvailable(ticketId: string): boolean {
    return this.domain.isTicketAvailable(ticketId)
  }

  getMaxAvailableQtyForTicket(ticketId: string): number {
    return this.domain.getMaxAvailableQtyForTicket(ticketId)
  }

  getLowestPriceForTicket(ticketId: string): Money | null {
    const availability: AvailabilityTime[] = this.getAvailabilityForTicket(ticketId)

    let allPrices: Money[] = availability.flatMap((slot) =>
      slot.prices?.filter((p) => p.resourceOptionId === ticketId).flatMap((p) => p.pricing.map((pr) => pr.listPrice))
    )

    if (!allPrices.length) {
      allPrices = this.getPricesForTicket(ticketId)
    }

    if (!allPrices.length) {
      return null
    }

    const minPrice = allPrices.reduce((currentLowestPrice, newPrice) => {
      const newAmount = parseFloat(newPrice?.amount)
      let currentLowestAmount = parseFloat(currentLowestPrice?.amount ?? '-1')
      currentLowestAmount = currentLowestAmount === -1 ? Infinity : currentLowestAmount

      return newAmount < currentLowestAmount ? newPrice : currentLowestPrice
    }, allPrices[0])

    return minPrice ?? null
  }
}

declare global {
  export type TicketsStore = TicketsStoreClass
  export interface StoreRegistry {
    tickets: TicketsStore
  }
}
